import { PublicProfile } from '@/lib/types/public-profile'

type SearchApiProfile = {
  id: string
  name?: string
  title?: string
  location?: string
  experience?: string | number | null
  sectors?: string[]
  skills?: string[]
  roles?: string[]
  bio?: string
  imageUrl?: string | null
  availability?: string
  users?: { first_name?: string; last_name?: string } | null
  user?: { first_name?: string; last_name?: string } | null
  is_active?: boolean
  profile_completed?: boolean
}

function normalizeName(p: SearchApiProfile): string {
  if (p.name && p.name.trim()) return p.name.trim()
  const first = p.users?.first_name ?? p.user?.first_name ?? ''
  const last = p.users?.last_name ?? p.user?.last_name ?? ''
  const full = `${first} ${last}`.trim()
  return full || 'Executive Profile'
}

function mapExperienceToReadable(experience?: string | number | null): string {
  if (typeof experience === 'number') {
    if (experience < 5) return '0-5 years'
    if (experience < 10) return '5-10 years'
    if (experience < 20) return '10-20 years'
    if (experience < 25) return '20-25 years'
    return '25+ years'
  }
  if (typeof experience === 'string' && experience.trim()) return experience
  return '10+ years'
}

function mapExperienceToCategory(experience?: string | number | null): string {
  const readable = mapExperienceToReadable(experience)
  const lower = readable.toLowerCase()
  if (lower.includes('0-5')) return 'junior'
  if (lower.includes('5-10')) return 'mid'
  if (lower.includes('10-20')) return 'senior'
  if (lower.includes('20-25')) return 'lead'
  if (lower.includes('25+')) return 'executive'
  return 'executive'
}

function normalizeArray(values?: string[] | null): string[] {
  if (!values || !Array.isArray(values)) return []
  return values
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .map(v => v.trim())
}

export function mapSearchApiProfileToPublicProfile(p: SearchApiProfile): PublicProfile {
  const name = normalizeName(p)
  const title = (p.title && p.title.trim()) || 'Executive'
  const sectors = normalizeArray(p.sectors).map(s => s.toLowerCase())
  const skills = normalizeArray(p.skills)
  const roles = normalizeArray(p.roles)
  const experienceReadable = mapExperienceToReadable(p.experience)
  const category = mapExperienceToCategory(p.experience)

  return {
    id: p.id,
    name,
    title,
    category,
    sectors,
    image: p.imageUrl ?? null,
    bio: (p.bio && p.bio.trim()) || 'Profile summary not available.',
    experience: experienceReadable,
    location: p.location || 'Not specified',
    skills,
    availability: p.availability || 'Available',
    roles,
  }
}

export function mapSearchApiProfilesToPublicProfiles(list: SearchApiProfile[] = []): PublicProfile[] {
  return list
    .filter(item => item && typeof item === 'object')
    .map(item => {
      try {
        return mapSearchApiProfileToPublicProfile(item)
      } catch (err) {
        console.error('Failed to map profile', item?.id, err)
        return null
      }
    })
    .filter((p): p is PublicProfile => !!p)
}

// Ensures only profiles explicitly marked active and completed are used upstream
export function filterActiveCompletedProfiles<T extends { is_active?: boolean; profile_completed?: boolean }>(
  list: T[] | undefined | null,
): T[] {
  if (!Array.isArray(list)) return []
  return list.filter(item => item?.is_active === true && item?.profile_completed === true)
}


